import type {
    ArgumentsSchema,
    AvailableFieldBlockInfo,
    AvailableFilterBlockInfo,
    Field,
    IndependentAction,
    PageVariableSchema,
    StateSchema,
    TypeInstanceMap,
    VariableADTvalue,
    ViewType
} from '@lighthouse/core'
import { type DataSourceAbstract, VariableType } from '@lighthouse/core'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { MatchEnableInnerType, USER_DATASOURCE } from '../../constants'
import type { FlowNode, Workflow } from '../../types'
import { getIdVariableData } from '../../utils'
import {
    getDefaultDataSourceOptions,
    getFilterControllerVariableOption,
    getImageVariableOption,
    getInputVariableOption,
    getOperatorOption,
    getPageParameterAndVariableOptions,
    getUpstreamActionsOptions,
    getUpstreamNodesOptions,
    getUserDatasourceOptions,
    getViewVariableOption,
    VariableSelect
} from '../'
import * as SC from './styles'

interface FieldVariableProps {
    pageVariables?: PageVariableSchema[]
    pagePathParameter?: ArgumentsSchema[]
    pageQueryParameter?: ArgumentsSchema[]
    pageStateParameter?: StateSchema[]
    upstreamNodes?: FlowNode[]
    allUpstreamNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    availableFieldBlocksInfo?: AvailableFieldBlockInfo[]
    availableFilterBlocksInfo?: AvailableFilterBlockInfo[]
    dataSourceList: DataSourceAbstract[]
    workflowList?: Workflow[]
    dataSource?: DataSourceAbstract
    viewDsId?: string
    viewType?: ViewType
    extraViewFields?: Field[]
    isUseImage?: boolean
    prefixName: string
    disabled?: boolean
    onMouseEnter?: (blockId: string) => void
    onMouseLeave?: () => void
}

export const FieldVariable: React.FC<FieldVariableProps> = ({
    pagePathParameter,
    pageQueryParameter,
    pageVariables,
    pageStateParameter,
    upstreamNodes,
    allUpstreamNodes,
    allParentActions,
    availableFieldBlocksInfo = [],
    availableFilterBlocksInfo = [],
    dataSourceList = [],
    workflowList = [],
    dataSource,
    viewType,
    viewDsId,
    extraViewFields,
    isUseImage,
    prefixName,
    disabled,
    onMouseEnter,
    onMouseLeave
}) => {
    const { control, setValue } = useFormContext()
    const pageOptions = useMemo(
        () =>
            getPageParameterAndVariableOptions({
                pagePathParameter,
                pageQueryParameter,
                pageVariables,
                pageStateParameter,
                dataSourceList
            }),
        [dataSourceList, pagePathParameter, pageQueryParameter, pageStateParameter, pageVariables]
    )
    const upstreamOptions = useMemo(() => {
        if (allParentActions) {
            return getUpstreamActionsOptions({ workflowList, allParentActions, dataSourceList })
        }
        return getUpstreamNodesOptions({ workflowList, upstreamNodes, allUpstreamNodes, dataSourceList })
    }, [workflowList, allParentActions, dataSourceList, upstreamNodes, allUpstreamNodes])

    const inputOption = useMemo(() => {
        if (availableFieldBlocksInfo.length === 0) {
            return
        }
        return getInputVariableOption(availableFieldBlocksInfo, dataSourceList, {
            onMouseEnter,
            onMouseLeave
        })
    }, [dataSourceList, availableFieldBlocksInfo, onMouseEnter, onMouseLeave])

    const filterOption = useMemo(() => {
        if (availableFilterBlocksInfo.length === 0) {
            return
        }
        return getFilterControllerVariableOption(availableFilterBlocksInfo, {
            onMouseEnter,
            onMouseLeave
        })
    }, [availableFilterBlocksInfo, onMouseEnter, onMouseLeave])

    const imageOption = useMemo(() => {
        if (isUseImage) {
            return getImageVariableOption()
        }
    }, [isUseImage])

    const userOption = useMemo(() => {
        const userDataSource = dataSourceList.find(item => item.id === USER_DATASOURCE)
        return getUserDatasourceOptions({
            dataSource: userDataSource
        })
    }, [dataSourceList])

    const viewOptions = useMemo(() => {
        const viewDataSource = dataSourceList.find(item => item.id === viewDsId)
        const viewOption = getViewVariableOption({
            dataSource: viewDataSource,
            viewType,
            extraFields: extraViewFields
        })

        return viewOption ? [viewOption] : undefined
    }, [dataSourceList, extraViewFields, viewDsId, viewType])

    const datasourceOption = useMemo(() => {
        return getDefaultDataSourceOptions(dataSource)
    }, [dataSource])

    const handleSetInitConditionByInnerType = useCallback(
        (innerType: TypeInstanceMap | undefined) => {
            const apply = () => {
                const op = getOperatorOption(innerType)
                // 数值和文本的默认操作符值都是 '='，必须单独把 paramList 换成新对象，旧值才会掉
                setValue(`${prefixName}.operator`, op[0]?.value ?? '', { shouldDirty: true })
                setValue(`${prefixName}.paramList`, [{ type: VariableType.VALUE }], { shouldDirty: true })
            }
            apply()
            // 右侧输入在字段类型变化后的 effect 里还会把旧值写回，晚于本次清空
            window.setTimeout(apply, 0)
        },
        [prefixName, setValue]
    )

    const resolveInnerType = useCallback(
        (variable?: VariableADTvalue): TypeInstanceMap | undefined => {
            if (!variable) {
                return
            }
            if (variable.type === VariableType.SYSTEM) {
                return 'DATE'
            }
            return getIdVariableData({
                idVariable: variable,
                dataSourceList,
                sourceFlowNode: upstreamNodes,
                dataSource,
                availableFieldBlocksInfo,
                availableFilterBlocksInfo,
                viewDataSource: dataSourceList.find(item => item.id === viewDsId),
                extraViewFields,
                pageVariableSchemas: pageVariables,
                pathParameter: pagePathParameter,
                queryParameter: pageQueryParameter,
                stateParameter: pageStateParameter,
                workflowList
            })?.innerType
        },
        [
            availableFieldBlocksInfo,
            availableFilterBlocksInfo,
            dataSource,
            dataSourceList,
            extraViewFields,
            pagePathParameter,
            pageQueryParameter,
            pageStateParameter,
            pageVariables,
            upstreamNodes,
            viewDsId,
            workflowList
        ]
    )

    const idVariable = useWatch({ control, name: `${prefixName}.idVariable` }) as VariableADTvalue | undefined
    const variableKey = JSON.stringify(idVariable ?? null)
    const innerType = resolveInnerType(idVariable)
    const typeSnapshotRef = useRef<{ prefixName: string; key: string; innerType?: TypeInstanceMap } | null>(null)

    const shouldInitCondition = useCallback((previous?: TypeInstanceMap, next?: TypeInstanceMap) => {
        return !previous || previous !== next
    }, [])

    // 右侧输入卸载时会把旧值写回。effect 在那之后再清一次，避免第一次保存被旧 paramList 盖掉
    useEffect(() => {
        const prev = typeSnapshotRef.current
        if (!prev || prev.prefixName !== prefixName) {
            typeSnapshotRef.current = { prefixName, key: variableKey, innerType }
            return
        }
        typeSnapshotRef.current = { prefixName, key: variableKey, innerType }
        if (prev.key === variableKey && prev.innerType === innerType) {
            return
        }
        // 同一个变量，schema 后到才解析出类型：补齐类型，不清已有条件
        if (prev.key === variableKey && !prev.innerType) {
            return
        }
        if (shouldInitCondition(prev.innerType, innerType)) {
            handleSetInitConditionByInnerType(innerType)
        }
    }, [handleSetInitConditionByInnerType, innerType, prefixName, shouldInitCondition, variableKey])

    return (
        <Controller
            name={`${prefixName}.idVariable`}
            control={control}
            render={({ field: ctlField }) => {
                return (
                    <SC.Box key={`${prefixName}.idVariable`}>
                        <VariableSelect
                            innerTypes={MatchEnableInnerType}
                            style={{ minWidth: 120, whiteSpace: 'nowrap' }}
                            disablePexels
                            datasourceOption={datasourceOption}
                            pageOptions={pageOptions}
                            upstreamOptions={upstreamOptions}
                            inputOption={inputOption}
                            filterOption={filterOption}
                            viewOptions={viewOptions}
                            userOption={userOption}
                            imageOption={imageOption}
                            disabled={disabled}
                            width="100%"
                            value={ctlField.value}
                            onChange={val => {
                                const previousInnerType = resolveInnerType(ctlField.value as VariableADTvalue | undefined)
                                const nextInnerType = resolveInnerType(val)
                                ctlField.onChange(val)
                                if (shouldInitCondition(previousInnerType, nextInnerType)) {
                                    handleSetInitConditionByInnerType(nextInnerType)
                                }
                                onMouseLeave?.()
                            }}
                        />
                    </SC.Box>
                )
            }}
        />
    )
}
